/** @jsxImportSource @emotion/react */
import { Global } from "@emotion/react";
import { useState, useRef, useEffect } from "react";
import {
  accessAPI,
  downloadFile,
  subirArchivosPesadosS3,
  useEstetica,
  useOrganismo,
  deOrganismo,
  tituloBase
} from "../../shared";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Boton, Titulo } from "../../shared";
import { Card } from "primereact/card";
import { ProgressBar } from "primereact/progressbar";
import { FormularioCampo } from "../../shared";

// Constante que representa la opción "ingresar uno nuevo" en los dropdowns
const NUEVO = "-1";
function esNuevo(valor) {
  return valor === NUEVO || valor === -1;
}

export default function NuevoProceso() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Nueva solicitud";
    return () => (document.title = tituloBase());
  }, []);

  const organismo = useOrganismo();
  const estetica = useEstetica();
  // El portal público del organismo, que es donde viven los reglamentos.
  const urlDocumentos = estetica?.urlPublica
    ? `${estetica.urlPublica}/documentos`
    : null;
  const [seleccionActual, setSeleccionActual] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [progresoSubida, setProgresoSubida] = useState(null);
  const [procesoListo, setProcesoListo] = useState(false);
  const [loaderDescargaTemplate, setLoaderDescargaTemplate] = useState(false);
  const [archivosAdicionales, setArchivosAdicionales] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // El paso más avanzado al que llegó: los pasos hasta ahí que estén
  // completos se muestran con tilde aunque se esté editando uno anterior, y
  // si ya llegó al resumen se ofrece "Volver al resumen".
  const [pasoMasLejano, setPasoMasLejano] = useState(0);
  const [opcionesTipoProceso, setOpcionesTipoProceso] = useState([]);
  const [opcionesCertificados, setOpcionesCertificados] = useState([]);
  const [opcionesFabricas, setOpcionesFabricas] = useState([]);
  const [opcionesFabricantes, setOpcionesFabricantes] = useState([]);
  const [tieneSabana, setTieneSabana] = useState(false);

  // Fábricas adicionales del paso 4 (siempre datos nuevos)
  const [fabricasAdicionales, setFabricasAdicionales] = useState([]);
  const [nuevaFabricaAdicional, setNuevaFabricaAdicional] = useState({
    nombre: "",
    direccion: ""
  });

  const inputProductosRef = useRef(null);
  const inputAdicionalesRef = useRef(null);
  const toast = useRef(null);

  const navigate = useNavigate();

  // Al cambiar de paso, arriba de todo: en mobile el paso nuevo puede quedar
  // fuera de la vista si se venía de uno largo.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setPasoMasLejano((anterior) => Math.max(anterior, activeIndex));
  }, [activeIndex]);

  // Cargar opciones de tipo de proceso
  useEffect(() => {
    accessAPI(
      "GET",
      "cliente/proceso/tipo?concertificadotipoparasolicitud=true",
      null,
      (respuesta) => setOpcionesTipoProceso(respuesta),
      (error) => console.error("Error cargando tipos de proceso:", error)
    );
  }, []);

  // Cargar opciones de certificados cuando cambia el tipo de proceso
  useEffect(() => {
    if (seleccionActual.procesoTipo) {
      accessAPI(
        "GET",
        `cliente/proceso/vigentes/${seleccionActual.procesoTipo}?parasolicitud=true`,
        null,
        (respuesta) => setOpcionesCertificados(respuesta),
        (error) => console.error("Error cargando certificados:", error)
      );
    }
  }, [seleccionActual.procesoTipo]);

  // Consulta cobertura sábana cuando cambia el tipo de certificado
  useEffect(() => {
    const certId = seleccionActual.certificadotipoid;
    if (!certId) {
      setTieneSabana(false);
      return;
    }
    accessAPI(
      "GET",
      `cliente/propuesta/sabana/cobertura/${certId}`,
      null,
      (respuesta) => setTieneSabana(respuesta?.tiene === true),
      () => setTieneSabana(false)
    );
  }, [seleccionActual.certificadotipoid]);

  // Cargar opciones de fábricas y fabricantes existentes del cliente
  useEffect(() => {
    accessAPI(
      "GET",
      "cliente/proceso/fabricas",
      null,
      (respuesta) => setOpcionesFabricas(respuesta),
      (error) => console.error("Error cargando fábricas:", error)
    );
    accessAPI(
      "GET",
      "cliente/proceso/fabricantes",
      null,
      (respuesta) => setOpcionesFabricantes(respuesta),
      (error) => console.error("Error cargando fabricantes:", error)
    );
  }, []);

  // Devuelve true si el proceso está listo para crearse
  useEffect(() => {
    const fabricaOk =
      (seleccionActual.sitioFabricaId &&
        !esNuevo(seleccionActual.sitioFabricaId)) ||
      (esNuevo(seleccionActual.sitioFabricaId) &&
        seleccionActual.fabricaNombre &&
        seleccionActual.fabricaDireccion);
    const fabricanteOk =
      (seleccionActual.sitioFabricanteId &&
        !esNuevo(seleccionActual.sitioFabricanteId)) ||
      (esNuevo(seleccionActual.sitioFabricanteId) &&
        seleccionActual.fabricanteNombre &&
        seleccionActual.fabricanteDireccion);

    setProcesoListo(
      !!(
        seleccionActual.procesoTipo &&
        seleccionActual.certificadotipoid &&
        fabricaOk &&
        fabricanteOk &&
        seleccionActual.nombrefantasia &&
        seleccionActual.archivoProductos
      )
    );
  }, [seleccionActual]);

  function actualizarCampo(nombre, valor) {
    // Se parte siempre del estado más reciente: varios campos pueden avisar
    // a la vez (al montarse) y con una copia vieja se pisarían entre sí.
    setSeleccionActual((anterior) => calcularSeleccion(anterior, nombre, valor));
  }

  function calcularSeleccion(anterior, nombre, valor) {
    // FormularioCampo, al volver a mostrarse (por ejemplo al ir a "editar" un
    // paso desde el resumen), avisa su valor actual como si fuera un cambio.
    // Si el valor es el mismo no se hace nada: si no, la cascada de abajo
    // borraba los pasos siguientes aunque el usuario no hubiera tocado nada.
    if (String(anterior[nombre] ?? "") === String(valor ?? "")) {
      return anterior;
    }

    const nuevaSeleccion = { ...anterior, [nombre]: valor };

    // La única dependencia entre pasos: la lista de tipos de certificado sale
    // del tipo de proceso, así que si este cambia el certificado elegido ya
    // no vale y hay que volver a elegirlo. Fabricante, fábrica, fábricas
    // adicionales, archivos y nombre son datos del cliente que no dependen
    // del tipo (las fábricas se listan sin filtro), así que se conservan.
    if (nombre === "procesoTipo") {
      nuevaSeleccion.certificadotipoid = null;
    }
    // Si elige una fábrica existente, limpiar datos de nueva
    if (nombre === "sitioFabricaId" && !esNuevo(valor)) {
      nuevaSeleccion.fabricaNombre = null;
      nuevaSeleccion.fabricaDireccion = null;
    }
    // Si elige un fabricante existente, limpiar datos de nuevo
    if (nombre === "sitioFabricanteId" && !esNuevo(valor)) {
      nuevaSeleccion.fabricanteNombre = null;
      nuevaSeleccion.fabricanteDireccion = null;
    }

    return nuevaSeleccion;
  }

  function agregarFabricaAdicional() {
    const nombre = nuevaFabricaAdicional.nombre?.trim();
    const direccion = nuevaFabricaAdicional.direccion?.trim();
    if (!nombre || !direccion) return;
    setFabricasAdicionales((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), nombre, direccion }
    ]);
    setNuevaFabricaAdicional({ nombre: "", direccion: "" });
  }

  function eliminarFabricaAdicional(fabrica) {
    setFabricasAdicionales((prev) => prev.filter((f) => f.id !== fabrica.id));
  }

  function crearProceso() {
    if (enviando) return;

    setEnviando(true);
    if (!procesoListo) {
      setEnviando(false);
      return;
    }

    const usarFabricaNueva = esNuevo(seleccionActual.sitioFabricaId);
    const usarFabricanteNuevo = esNuevo(seleccionActual.sitioFabricanteId);

    accessAPI(
      "POST",
      `cliente/proceso/solicitud`,
      {
        certificadotipoid: seleccionActual.certificadotipoid,
        nombrefantasia: seleccionActual.nombrefantasia,
        sitioFabricaId: usarFabricaNueva
          ? null
          : seleccionActual.sitioFabricaId,
        sitioFabricanteId: usarFabricanteNuevo
          ? null
          : seleccionActual.sitioFabricanteId,
        fabricaNombre: usarFabricaNueva ? seleccionActual.fabricaNombre : null,
        fabricaDireccion: usarFabricaNueva
          ? seleccionActual.fabricaDireccion
          : null,
        fabricanteNombre: usarFabricanteNuevo
          ? seleccionActual.fabricanteNombre
          : null,
        fabricanteDireccion: usarFabricanteNuevo
          ? seleccionActual.fabricanteDireccion
          : null,
        fabricasAdicionales: fabricasAdicionales.map((f) => ({
          nombre: f.nombre,
          direccion: f.direccion
        }))
      },
      (respuesta) => {
        const solicitudId = respuesta.id;
        if (archivosAdicionales.length === 0) {
          navigate("/", {
            state: {
              showToast: true,
              toastMessage: "Solicitud ingresada correctamente"
            }
          });
          return;
        }
        subirArchivosAdicionales(solicitudId);
      },
      (response) => {
        setEnviando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: response[0]?.msg || "Error al crear la solicitud",
          sticky: true
        });
      },
      seleccionActual.archivoProductos
    );
  }

  function subirArchivosAdicionales(solicitudId) {
    // Subida directa a S3 (presigned) de todos los archivos adicionales en batch.
    // Soporta archivos pesados (evita el timeout del frontend y el doble salto).
    const archivos = archivosAdicionales.map((a) => a.file || a);
    setProgresoSubida(0);

    subirArchivosPesadosS3({
      presignEndpoint: "cliente/archivo/presign",
      confirmarEndpoint: `cliente/proceso/solicitud/${solicitudId}/archivo/confirmar`,
      archivos,
      datosPresign: { ubicacion: "archivosprocesos" },
      onProgress: (porcentaje) => setProgresoSubida(porcentaje)
    })
      .then(() => {
        navigate("/", {
          state: {
            showToast: true,
            toastMessage: "Solicitud ingresada correctamente"
          }
        });
      })
      .catch((error) => {
        setEnviando(false);
        setProgresoSubida(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.[0]?.msg || "Error al subir los archivos adicionales",
          sticky: true
        });
      });
  }

  function descargarTemplate() {
    if (!seleccionActual.certificadotipoid) {
      console.error("Debe seleccionar un tipo de certificado");
      return;
    }

    setLoaderDescargaTemplate(true);

    downloadFile(
      `cliente/proceso/plantilla/${seleccionActual.certificadotipoid}`,
      "Plantilla Productos.xlsx",
      () => setLoaderDescargaTemplate(false),
      (respuesta) => {
        console.error("Error al descargar plantilla:", respuesta);
        setLoaderDescargaTemplate(false);
      }
    );
  }

  function seleccionarArchivo(event) {
    const archivo = event.target.files[0];
    if (archivo)
      setSeleccionActual((anterior) => ({ ...anterior, archivoProductos: archivo }));
    if (inputProductosRef.current) inputProductosRef.current.value = "";
  }

  function seleccionarArchivosAdicionales(event) {
    const archivos = Array.from(event.target.files);
    const extensionesPermitidas = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "webp",
      "pdf",
      "xls",
      "xlsx",
      "xlsm",
      "csv",
      "doc",
      "docx",
      "odt",
      "ods",
      "txt"
    ];

    const archivosValidos = [];
    archivos.forEach((archivo) => {
      const extension = archivo.name.split(".").pop().toLowerCase();
      if (extensionesPermitidas.includes(extension)) {
        archivosValidos.push(archivo);
      }
    });

    // Antes los archivos de tipo no permitido se descartaban sin aviso
    const descartados = archivos.length - archivosValidos.length;
    if (descartados > 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Archivos no agregados",
        detail: `${descartados} ${descartados === 1 ? "archivo no es" : "archivos no son"} de un tipo permitido.`,
        life: 5000
      });
    }

    if (archivosValidos.length > 0) {
      const archivosConId = archivosValidos.map((archivo, index) => ({
        uniqueId: `${Date.now()}_${index}_${Math.random()}`,
        name: archivo.name,
        size: archivo.size,
        type: archivo.type,
        file: archivo
      }));
      setArchivosAdicionales((prev) => [...prev, ...archivosConId]);
    }

    if (inputAdicionalesRef.current) inputAdicionalesRef.current.value = "";
  }

  function eliminarArchivoAdicional(archivo) {
    setArchivosAdicionales((prev) =>
      prev.filter((a) => a.uniqueId !== archivo.uniqueId)
    );
  }

  function accionesArchivo(rowData) {
    return (
      <Boton
        callback={() => eliminarArchivoAdicional(rowData)}
        chico
        icono
        rojo
      >
        <i className="pi pi-trash"></i>
      </Boton>
    );
  }

  function accionesFabricaAdicional(rowData) {
    return (
      <Boton
        callback={() => eliminarFabricaAdicional(rowData)}
        chico
        icono
        rojo
      >
        <i className="pi pi-trash"></i>
      </Boton>
    );
  }

  // Etiquetas para resumen
  function obtenerEtiquetaTipoProceso() {
    if (!seleccionActual.procesoTipo || opcionesTipoProceso.length === 0)
      return "No seleccionado";
    const opcion = opcionesTipoProceso.find(
      (t) => t.value === seleccionActual.procesoTipo
    );
    return opcion ? opcion.etiqueta : seleccionActual.procesoTipo;
  }

  function obtenerEtiquetaCertificado() {
    if (!seleccionActual.certificadotipoid || opcionesCertificados.length === 0)
      return "No seleccionado";
    const opcion = opcionesCertificados.find(
      (c) => c.value === seleccionActual.certificadotipoid
    );
    return opcion
      ? opcion.etiqueta
      : `ID: ${seleccionActual.certificadotipoid}`;
  }

  function obtenerEtiquetaFabrica() {
    if (esNuevo(seleccionActual.sitioFabricaId)) {
      return `${seleccionActual.fabricaNombre || "Sin nombre"} - ${
        seleccionActual.fabricaDireccion || "Sin dirección"
      } (nueva)`;
    }
    const opcion = opcionesFabricas.find(
      (f) => f.value === seleccionActual.sitioFabricaId
    );
    return opcion ? opcion.etiqueta : "No seleccionada";
  }

  function obtenerEtiquetaFabricante() {
    if (esNuevo(seleccionActual.sitioFabricanteId)) {
      return `${seleccionActual.fabricanteNombre || "Sin nombre"} - ${
        seleccionActual.fabricanteDireccion || "Sin dirección"
      } (nuevo)`;
    }
    const opcion = opcionesFabricantes.find(
      (f) => f.value === seleccionActual.sitioFabricanteId
    );
    return opcion ? opcion.etiqueta : "No seleccionado";
  }

  // Las opciones del dropdown agregan la opción "nuevo" al inicio
  const opcionesFabricaConNuevo = [
    { value: NUEVO, etiqueta: "Ingresar una nueva" },
    ...opcionesFabricas
  ];
  const opcionesFabricanteConNuevo = [
    { value: NUEVO, etiqueta: "Ingresar uno nuevo" },
    ...opcionesFabricantes
  ];

  // Validaciones para avanzar en los pasos
  const puedeAvanzarPaso1 = !!seleccionActual?.procesoTipo;
  const puedeAvanzarPaso2 =
    puedeAvanzarPaso1 && !!seleccionActual?.certificadotipoid;
  const fabricaOkPaso3 =
    (seleccionActual.sitioFabricaId &&
      !esNuevo(seleccionActual.sitioFabricaId)) ||
    (esNuevo(seleccionActual.sitioFabricaId) &&
      seleccionActual.fabricaNombre &&
      seleccionActual.fabricaDireccion);
  const fabricanteOkPaso3 =
    (seleccionActual.sitioFabricanteId &&
      !esNuevo(seleccionActual.sitioFabricanteId)) ||
    (esNuevo(seleccionActual.sitioFabricanteId) &&
      seleccionActual.fabricanteNombre &&
      seleccionActual.fabricanteDireccion);
  const puedeAvanzarPaso3 =
    puedeAvanzarPaso2 && fabricaOkPaso3 && fabricanteOkPaso3;
  // Paso 4 (fábricas adicionales) siempre se puede saltar
  const puedeAvanzarPaso4 = puedeAvanzarPaso3;
  const puedeAvanzarPaso5 =
    puedeAvanzarPaso4 && !!seleccionActual.archivoProductos;
  const puedeAvanzarPaso6 =
    puedeAvanzarPaso5 && !!seleccionActual.nombrefantasia;

  // Hasta qué paso se puede llegar: el primero cuya validación no pasa. Los
  // pasos anteriores a ese se pueden visitar desde el indicador de progreso.
  // Validez PROPIA de cada paso (sin mirar los anteriores), para los tildes
  // del indicador: si se cambia el tipo de proceso solo se apaga el del tipo
  // de certificado, que es el único que queda vacío.
  const pasoValido = [
    !!seleccionActual?.procesoTipo,
    !!seleccionActual?.certificadotipoid,
    !!(fabricaOkPaso3 && fabricanteOkPaso3),
    true,
    !!seleccionActual.archivoProductos,
    !!seleccionActual.nombrefantasia
  ];

  const puedeAvanzar = [
    puedeAvanzarPaso1,
    puedeAvanzarPaso2,
    puedeAvanzarPaso3,
    puedeAvanzarPaso4,
    puedeAvanzarPaso5,
    puedeAvanzarPaso6
  ];
  const ultimoAlcanzable = (() => {
    const i = puedeAvanzar.findIndex((ok) => !ok);
    return i === -1 ? PASOS.length - 1 : i;
  })();
  const esUltimo = activeIndex === PASOS.length - 1;

  const editarDesdeResumen = (paso) => setActiveIndex(paso);
  const irAlResumen = () => setActiveIndex(PASOS.length - 1);
  // "Volver al resumen" en lugar de "Siguiente" si ya se llegó al resumen
  // alguna vez y todo sigue completo, sin importar cómo se volvió al paso
  // (lápiz del resumen o número del indicador). Si al corregir quedó un paso
  // incompleto, hay que pasar por él con "Siguiente".
  const puedeVolverAlResumen =
    pasoMasLejano === PASOS.length - 1 &&
    !esUltimo &&
    ultimoAlcanzable === PASOS.length - 1;

  return (
    <div css={estilos.asistente}>
      {/* El panel de los desplegables se monta en body, fuera del asistente */}
      <Global styles={estilos.panelDesplegable} />
      <Toast ref={toast} position="top-center" />
      {/* Tooltip de PrimeReact para los lápices de editar del resumen (uno solo
          para todos) */}
      <Tooltip target=".boton-editar-resumen" position="left" />
      <>
        <Titulo
          botones={
            <Boton
              rojo
              transparente
              uri="/"
              confirmacion="Si cancela la solicitud perderá los datos ingresados, ¿seguro?"
            >
              Cancelar solicitud
            </Boton>
          }
        >
          Nueva solicitud de certificación
        </Titulo>
        <Progreso
          activo={activeIndex}
          ultimoAlcanzable={ultimoAlcanzable}
          completo={(i) => i < PASOS.length - 1 && i <= pasoMasLejano && pasoValido[i]}
          onIr={setActiveIndex}
        />

        <Card css={estilos.paso}>
          <Titulo>
            {activeIndex + 1}. {PASOS[activeIndex]}
          </Titulo>

          {/* Paso 1: Tipo de proceso */}
          {activeIndex === 0 && (
            <>
              <p>
                <b>
                  En este formulario deberá completar los campos necesarios
                  para comenzar un nuevo proceso de certificación.
                </b>
              </p>
              <p>
                Si no está seguro de qué completar en algún paso, no dude en
                consultarnos directamente. De cualquier manera, las selecciones
                que realice pueden ser revisadas una vez creado el proceso.
              </p>
              <p>
                El primer paso consiste en seleccionar un <b>tipo de proceso</b>
                . Esto sería la categoría del certificado, permite luego filtrar
                el listado de tipos de certificado.
              </p>
              <div css={{ marginTop: "1.25rem" }}>
                <FormularioCampo
                  campo={{
                    nombre: "procesoTipo",
                    etiqueta: "Tipo de proceso",
                    tipoitemformularioid: 5,
                    opciones:
                      "cliente/proceso/tipo?concertificadotipoparasolicitud=true",
                    sinOpcionInicial: true,
                    respuesta: seleccionActual.procesoTipo
                  }}
                  onUpdate={actualizarCampo}
                  contenidoCampos={seleccionActual}
                />
              </div>
            </>
          )}

          {/* Paso 2: Tipo de certificado */}
          {activeIndex === 1 && (
            <>
              <p>
                El segundo paso consiste en seleccionar el{" "}
                <b>tipo de certificado</b>, esto es qué categoría de productos
                serán abarcados. Una vez creado el proceso podrá agregar los
                productos específicos a ser incluidos.
              </p>
              <div css={{ marginTop: "1.25rem" }}>
                <FormularioCampo
                  campo={{
                    nombre: "certificadotipoid",
                    etiqueta: "Tipo de certificado",
                    tipoitemformularioid: 5,
                    opciones: `cliente/proceso/vigentes/${seleccionActual.procesoTipo}?parasolicitud=true`,
                    sinOpcionInicial: true,
                    queryparams: ["procesoTipo"],
                    respuesta: seleccionActual.certificadotipoid
                  }}
                  onUpdate={actualizarCampo}
                  contenidoCampos={seleccionActual}
                />
              </div>
              {tieneSabana && <AvisoSabana />}
            </>
          )}

          {/* Paso 3: Fabricante y fábrica */}
          {activeIndex === 2 && (
            <>
              <p>
                Necesitamos conocer los datos del{" "}
                <b>fabricante y de la fábrica</b> de los productos que desea
                certificar.
              </p>
              <p>
                Para cada uno puede seleccionar uno ya cargado, o elegir
                "ingresar uno nuevo" y completar los datos.
              </p>

              <h3 css={estilos.subtitulo}>Fabricante</h3>
              <FormularioCampo
                campo={{
                  nombre: "sitioFabricanteId",
                  etiqueta: "Fabricante",
                  tipoitemformularioid: 5,
                  opciones: opcionesFabricanteConNuevo,
                  sinOpcionInicial: true,
                  respuesta: seleccionActual.sitioFabricanteId
                }}
                onUpdate={actualizarCampo}
                contenidoCampos={seleccionActual}
              />
              {esNuevo(seleccionActual.sitioFabricanteId) && (
                <div css={estilos.recuadro}>
                  <FormularioCampo
                    campo={{
                      nombre: "fabricanteNombre",
                      etiqueta: "Nombre del fabricante",
                      tipoitemformularioid: 1,
                      respuesta: seleccionActual.fabricanteNombre
                    }}
                    onUpdate={actualizarCampo}
                    contenidoCampos={seleccionActual}
                  />
                  <FormularioCampo
                    campo={{
                      nombre: "fabricanteDireccion",
                      etiqueta: "Dirección del fabricante",
                      tipoitemformularioid: 1,
                      respuesta: seleccionActual.fabricanteDireccion
                    }}
                    onUpdate={actualizarCampo}
                    contenidoCampos={seleccionActual}
                  />
                </div>
              )}

              <h3 css={estilos.subtitulo}>Fábrica</h3>
              <FormularioCampo
                campo={{
                  nombre: "sitioFabricaId",
                  etiqueta: "Fábrica",
                  tipoitemformularioid: 5,
                  opciones: opcionesFabricaConNuevo,
                  sinOpcionInicial: true,
                  respuesta: seleccionActual.sitioFabricaId
                }}
                onUpdate={actualizarCampo}
                contenidoCampos={seleccionActual}
              />
              {esNuevo(seleccionActual.sitioFabricaId) && (
                <div css={estilos.recuadro}>
                  <FormularioCampo
                    campo={{
                      nombre: "fabricaNombre",
                      etiqueta: "Nombre de la fábrica",
                      tipoitemformularioid: 1,
                      respuesta: seleccionActual.fabricaNombre
                    }}
                    onUpdate={actualizarCampo}
                    contenidoCampos={seleccionActual}
                  />
                  <FormularioCampo
                    campo={{
                      nombre: "fabricaDireccion",
                      etiqueta: "Dirección de la fábrica",
                      tipoitemformularioid: 1,
                      respuesta: seleccionActual.fabricaDireccion
                    }}
                    onUpdate={actualizarCampo}
                    contenidoCampos={seleccionActual}
                  />
                </div>
              )}
            </>
          )}

          {/* Paso 4: Fábricas adicionales (opcional) */}
          {activeIndex === 3 && (
            <>
              <p>
                Si el proceso involucra <b>más fábricas</b> además de la que
                ingresó en el paso anterior, agréguelas debajo. Quedarán
                registradas como sitios de la empresa para usarlas en futuras
                solicitudes.
              </p>
              <p>Este paso es opcional, puede continuar si no aplica.</p>

              <div css={estilos.recuadro}>
                <FormularioCampo
                  campo={{
                    nombre: "nombre",
                    etiqueta: "Nombre de la fábrica",
                    tipoitemformularioid: 1,
                    respuesta: nuevaFabricaAdicional.nombre
                  }}
                  onUpdate={(_, valor) =>
                    setNuevaFabricaAdicional((prev) => ({ ...prev, nombre: valor }))
                  }
                  contenidoCampos={nuevaFabricaAdicional}
                />
                <FormularioCampo
                  campo={{
                    nombre: "direccion",
                    etiqueta: "Dirección de la fábrica",
                    tipoitemformularioid: 1,
                    respuesta: nuevaFabricaAdicional.direccion
                  }}
                  onUpdate={(_, valor) =>
                    setNuevaFabricaAdicional((prev) => ({ ...prev, direccion: valor }))
                  }
                  contenidoCampos={nuevaFabricaAdicional}
                />
                <div css={{ marginTop: "1em", display: "flex", justifyContent: "flex-end" }}>
                  <Boton
                    transparente
                    disabled={
                      !nuevaFabricaAdicional.nombre?.trim() ||
                      !nuevaFabricaAdicional.direccion?.trim()
                    }
                    callback={agregarFabricaAdicional}
                  >
                    Agregar fábrica
                  </Boton>
                </div>
              </div>

              {fabricasAdicionales.length > 0 && (
                <ListaItems
                  items={fabricasAdicionales.map((f) => ({
                    id: f.id,
                    titulo: f.nombre,
                    detalle: f.direccion,
                    accion: accionesFabricaAdicional(f)
                  }))}
                />
              )}
            </>
          )}

          {/* Paso 5: Productos y archivos */}
          {activeIndex === 4 && (
            <>
              <p>
                El siguiente paso es especificar los productos que serán
                alcanzados por el certificado. Descargue la plantilla y
                complétela con los detalles técnicos de los productos.
              </p>

              <div css={estilos.bloques}>
                <div css={estilos.recuadro}>
                  <div css={estilos.datoEtiqueta}>1. Descargue la plantilla</div>
                  <div css={{ marginTop: "0.75rem" }}>
                    <Boton transparente loader={loaderDescargaTemplate} callback={descargarTemplate}>
                      Descargar plantilla
                    </Boton>
                  </div>
                </div>
                <div css={estilos.recuadro}>
                  <div css={estilos.datoEtiqueta}>
                    2. Suba la plantilla completa (obligatorio)
                  </div>
                  <input
                    ref={inputProductosRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={seleccionarArchivo}
                    style={{ display: "none" }}
                  />
                  <div css={{ marginTop: "0.75rem" }}>
                    <Boton callback={() => inputProductosRef.current?.click()}>
                      {seleccionActual.archivoProductos ? "Cambiar planilla" : "Subir plantilla completa"}
                    </Boton>
                  </div>
                  {seleccionActual.archivoProductos && (
                    <div css={estilos.archivoOk}>
                      <i className="pi pi-check-circle" />
                      <span>{seleccionActual.archivoProductos.name}</span>
                    </div>
                  )}
                </div>
              </div>

              <h3 css={estilos.subtitulo}>Documentación técnica adicional</h3>
              <p>
                Adjunte todos los documentos técnicos para el proceso. Pueden
                ser certificados, reportes de ensayo o manuales de los
                productos. Serán evaluados por el equipo auditor durante el
                proceso.
              </p>
              <p css={estilos.celdaSub}>
                Tipos permitidos: jpg, jpeg, png, gif, bmp, webp, pdf, xls,
                xlsx, xlsm, csv, doc, docx, odt, ods, txt.
              </p>
              <input
                ref={inputAdicionalesRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.xls,.xlsx,.xlsm,.csv,.doc,.docx,.odt,.ods,.txt"
                onChange={seleccionarArchivosAdicionales}
                style={{ display: "none" }}
              />
              <Boton transparente callback={() => inputAdicionalesRef.current?.click()}>
                Seleccionar archivos
              </Boton>
              {archivosAdicionales.length > 0 && (
                <ListaItems
                  items={archivosAdicionales.map((a) => ({
                    id: a.uniqueId || a.name,
                    titulo: a.name,
                    icono: "pi pi-file",
                    accion: accionesArchivo(a)
                  }))}
                />
              )}
            </>
          )}

          {/* Paso 6: Datos del proceso */}
          {activeIndex === 5 && (
            <>
              <p>
                Finalmente, asígnele un <b>nombre</b> al proceso para poder
                identificarlo luego, lo verá en la página principal al ingresar.
              </p>
              <div css={{ marginTop: "1.25rem" }}>
                <FormularioCampo
                  campo={{
                    nombre: "nombrefantasia",
                    etiqueta: "Nombre fantasía del proceso",
                    tipoitemformularioid: 1,
                    respuesta: seleccionActual.nombrefantasia,
                    requerido: true
                  }}
                  onUpdate={actualizarCampo}
                  contenidoCampos={seleccionActual}
                />
              </div>
            </>
          )}

          {/* Paso 7: Resumen y confirmación */}
          {activeIndex === 6 && (
            <>
              <p>Revise los datos antes de ingresar la solicitud.</p>
              {tieneSabana && <AvisoSabana />}
              <div css={estilos.resumen}>
                <ItemResumen titulo="Tipo de proceso" paso={0} onEditar={editarDesdeResumen}>
                  {obtenerEtiquetaTipoProceso()}
                </ItemResumen>
                <ItemResumen titulo="Tipo de certificado" paso={1} onEditar={editarDesdeResumen}>
                  {obtenerEtiquetaCertificado()}
                </ItemResumen>
                <ItemResumen titulo="Fabricante" paso={2} onEditar={editarDesdeResumen}>
                  {obtenerEtiquetaFabricante()}
                </ItemResumen>
                <ItemResumen titulo="Fábrica" paso={2} onEditar={editarDesdeResumen}>
                  {obtenerEtiquetaFabrica()}
                </ItemResumen>
                <ItemResumen titulo="Fábricas adicionales" paso={3} onEditar={editarDesdeResumen}>
                  {fabricasAdicionales.length === 0 ? (
                    "Ninguna"
                  ) : (
                    <ul css={{ margin: 0, paddingLeft: "1.1rem" }}>
                      {fabricasAdicionales.map((f) => (
                        <li key={f.id}>
                          {f.nombre} — {f.direccion}
                        </li>
                      ))}
                    </ul>
                  )}
                </ItemResumen>
                <ItemResumen titulo="Planilla de productos" paso={4} onEditar={editarDesdeResumen}>
                  {seleccionActual.archivoProductos?.name || "-"}
                </ItemResumen>
                <ItemResumen titulo="Archivos adicionales" paso={4} onEditar={editarDesdeResumen}>
                  {archivosAdicionales.length === 0
                    ? "Ninguno"
                    : `${archivosAdicionales.length} ${archivosAdicionales.length === 1 ? "archivo" : "archivos"}`}
                </ItemResumen>
                <ItemResumen titulo="Nombre del proceso" paso={5} onEditar={editarDesdeResumen}>
                  {seleccionActual.nombrefantasia || "No especificado"}
                </ItemResumen>
              </div>

              <p css={estilos.legal}>
                Al completar este formulario declaro ser representante de la
                empresa titular y estar en pleno conocimiento del reglamento y
                las condiciones de certificación publicadas en{" "}
                {urlDocumentos ? (
                  <a href={urlDocumentos} target="_blank" rel="noreferrer">
                    {urlDocumentos.replace(/^https?:\/\//, "")}
                  </a>
                ) : (
                  `el portal público ${deOrganismo(organismo)}`
                )}
              </p>
            </>
          )}

          {/* Navegación entre pasos: en mobile queda fija al pie */}
          <div css={{ marginTop: "1.75rem", paddingTop: "1.25rem", borderTop: "1px solid #e0e0e0" }}>
            {enviando && progresoSubida !== null && (
              <div css={{ fontSize: "0.82rem", color: "var(--grisTextos)", marginBottom: "0.6rem", textAlign: "right" }}>
                <i className="pi pi-spin pi-spinner" />{" "}
                {progresoSubida < 100 ? `Subiendo archivos… ${progresoSubida}%` : "Procesando…"}
              </div>
            )}
            {/* Anterior a la izquierda (o nada) y Siguiente a la derecha */}
            <div css={{ display: "flex", justifyContent: "space-between", gap: "0.75rem" }}>
              {activeIndex > 0 ? (
                <Boton transparente disabled={enviando} callback={() => setActiveIndex(activeIndex - 1)}>
                  Anterior
                </Boton>
              ) : (
                <span />
              )}
              {esUltimo ? (
                <Boton loader={enviando} disabled={!procesoListo} callback={crearProceso}>
                  Ingresar solicitud
                </Boton>
              ) : puedeVolverAlResumen ? (
                <Boton callback={irAlResumen}>
                  Volver al resumen
                </Boton>
              ) : (
                <Boton disabled={!puedeAvanzar[activeIndex]} callback={() => setActiveIndex(activeIndex + 1)}>
                  Siguiente
                </Boton>
              )}
            </div>
          </div>
        </Card>
      </>
    </div>
  );
}

// Estilos del asistente (antes .p-asistente-* en portal.css)
const TEXTO = "rgba(44, 44, 44, 1)";
const FONDO_SUAVE = "#f8f9fa";
const UNA_COLUMNA = "@media (max-width: 768px)";
const estilos = {
  // Desplegables: el texto elegido se ve completo (en varias líneas si hace
  // falta) en vez de cortarse con "…"
  asistente: {
    "& .p-dropdown": { width: "100%", height: "auto" },
    "& .p-dropdown .p-dropdown-label": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      lineHeight: 1.35
    }
  },
  // Las opciones también completas, y la lista sin salirse de la pantalla
  panelDesplegable: {
    ".p-dropdown-panel": { maxWidth: "calc(100vw - 2rem)" },
    ".p-dropdown-panel .p-dropdown-item": { whiteSpace: "normal", lineHeight: 1.35 }
  },
  paso: { marginBottom: "1em", [UNA_COLUMNA]: { "& .p-card-body": { padding: "1.1rem 1rem" } } },
  subtitulo: { fontSize: "0.95rem", fontWeight: 600, margin: "1.5rem 0 0.5rem" },
  recuadro: { marginTop: "1rem", padding: "1rem 1.1rem", background: FONDO_SUAVE, borderRadius: 8 },
  bloques: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "0 1rem",
    [UNA_COLUMNA]: { gridTemplateColumns: "minmax(0, 1fr)" }
  },
  datoEtiqueta: {
    fontSize: "0.68rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--grisTextos)"
  },
  celdaSub: { fontSize: "0.78rem", color: "var(--grisTextos)" },
  archivoOk: {
    marginTop: "0.75rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.55rem 0.75rem",
    borderRadius: 8,
    background: "var(--verdeLight)",
    color: "var(--verdeAcento)",
    fontWeight: 600,
    wordBreak: "break-word"
  },
  aviso: {
    marginTop: "1rem",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.6rem",
    padding: "0.75rem 1rem",
    borderRadius: 8,
    background: "var(--verdeLight)",
    color: "var(--verdeAcento)",
    fontWeight: 600
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.6rem 0.75rem",
    borderRadius: 8,
    background: FONDO_SUAVE
  },
  // Resumen en dos columnas, ordenado por columna (la primera de arriba hacia
  // abajo y después la segunda) para que se lea en el orden de los pasos.
  // Son 8 bloques: 4 filas. En mobile, una sola columna.
  resumen: {
    marginTop: "1rem",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gridTemplateRows: "repeat(4, auto)",
    gridAutoFlow: "column",
    gap: "0.6rem",
    [UNA_COLUMNA]: { gridTemplateColumns: "minmax(0, 1fr)", gridTemplateRows: "none", gridAutoFlow: "row" }
  },
  resumenItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "0.75rem",
    padding: "0.75rem 0.9rem",
    borderRadius: 8,
    background: FONDO_SUAVE
  },
  // Lápiz de "editar" en la esquina superior derecha de cada bloque del resumen
  editar: {
    flexShrink: 0,
    width: 32,
    height: 32,
    margin: "-0.35rem -0.4rem 0 0",
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "var(--principal)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { color: TEXTO }
  },
  legal: { marginTop: "1.25rem", fontSize: "0.86rem", color: "var(--grisTextos)", wordBreak: "break-word" }
};

const PASOS = [
  "Tipo de proceso",
  "Tipo de certificado",
  "Fabricante y fábrica",
  "Fábricas adicionales",
  "Productos y archivos",
  "Datos del proceso",
  "Resumen y confirmación"
];

// Indicador de avance: en escritorio los 7 pasos (los ya alcanzables se
// pueden clickear para volver); en mobile una barra, con el paso en el
// subtítulo. Estilos propios (antes .p-asistente-* en portal.css).
function Progreso({ activo, ultimoAlcanzable, completo, onIr }) {
  const porcentaje = Math.round(((activo + 1) / PASOS.length) * 100);
  return (
    <Card css={{ marginBottom: "1em" }}>
      <Titulo subtitulo={`Paso ${activo + 1} de ${PASOS.length}: ${PASOS[activo]}`}>Pasos de la solicitud</Titulo>
      <ProgressBar
        value={porcentaje}
        showValue={false}
        css={{
          height: "0.5rem",
          "& .p-progressbar-value": { background: "var(--principal)" },
          "@media (min-width: 1101px)": { display: "none" }
        }}
      />
      <ol
        css={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${PASOS.length}, minmax(0, 1fr))`,
          gap: "0.4rem",
          "@media (max-width: 1100px)": { display: "none" }
        }}
      >
        {PASOS.map((nombre, i) => {
          // Tilde: paso completo y ya visitado, esté antes o después del actual
          const hecho = completo(i) && i !== activo;
          const actual = i === activo;
          const clickeable = !actual && i <= ultimoAlcanzable;
          return (
            <li key={nombre}>
              <button
                type="button"
                disabled={!clickeable}
                onClick={() => onIr(i)}
                css={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  border: "none",
                  background: actual ? "var(--principalLight)" : "none",
                  borderRadius: 8,
                  padding: "0.45rem 0.5rem",
                  fontFamily: "inherit",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: actual ? "var(--principal)" : hecho ? "rgba(44, 44, 44, 1)" : "var(--grisTextos)",
                  textAlign: "left",
                  lineHeight: 1.25,
                  cursor: clickeable ? "pointer" : "default",
                  "&:not(:disabled):hover": { color: "var(--principal)" }
                }}
              >
                <span
                  css={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    background: actual ? "var(--principal)" : hecho ? "var(--verdeLight)" : "#eeeeee",
                    color: actual ? "#ffffff" : hecho ? "var(--verdeAcento)" : "var(--grisTextos)"
                  }}
                >
                  {hecho ? <i className="pi pi-check" css={{ fontSize: "0.75rem" }} /> : i + 1}
                </span>
                <span>{nombre}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

function AvisoSabana() {
  return (
    <div css={estilos.aviso}>
      <i className="pi pi-check-circle" />
      <span>Tenés un contrato sábana activo para este tipo de certificado.</span>
    </div>
  );
}

function ItemResumen({ titulo, paso, onEditar, children }) {
  return (
    <div css={estilos.resumenItem}>
      <div css={{ minWidth: 0 }}>
        <div css={estilos.datoEtiqueta}>
          {paso + 1} · {titulo}
        </div>
        <div css={{ fontWeight: 600, marginTop: "0.15rem", wordBreak: "break-word" }}>{children}</div>
      </div>
      <button
        type="button"
        // La clase es solo para el Tooltip
        className="boton-editar-resumen"
        css={estilos.editar}
        onClick={() => onEditar(paso)}
        aria-label={`Editar ${titulo.toLowerCase()}`}
        data-pr-tooltip="Editar"
      >
        <i className="pi pi-pencil" />
      </button>
    </div>
  );
}

// Lista simple (fábricas adicionales, archivos): en vez de una tabla, que en
// mobile no entra, filas con el texto y la acción de eliminar a la derecha.
function ListaItems({ items }) {
  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1rem" }}>
      {items.map((item) => (
        <div key={item.id} css={estilos.item}>
          {item.icono && <i className={item.icono} css={{ color: "var(--principalAcento)" }} />}
          <div css={{ minWidth: 0, flex: 1 }}>
            <div css={{ fontWeight: 600, color: TEXTO, wordBreak: "break-word" }}>{item.titulo}</div>
            {item.detalle && <div css={estilos.celdaSub}>{item.detalle}</div>}
          </div>
          {item.accion}
        </div>
      ))}
    </div>
  );
}
